import React, { useState } from "react";
import { Dropdown, Spinner, Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  useEticketingDeleteRouteMutation,
  useEticketingRoutesQuery,
} from "../../../../api/e-ticketing/e-ticketing.hooks";
import Pagination from "../../Pagination/Pagination";
import DeleteModal from "../../DeleteModal";
import RoutePrices from "./RoutePrices";
import RouteForm from "./RouteForm";
import { emojis } from "../../bootstrap/alertData";
import { getCurrentUser } from "../../PluginsMenu/Nestable/utils";
import { Can } from "../../../../context/Can";

const Routes = () => {
  const { serviceId, service } = useParams();
  const { city_id } = getCurrentUser();
  const { data, isLoading, error, refetch } = useEticketingRoutesQuery({
    service_id: serviceId,
    ...(city_id ? { cityId: city_id } : {}),
  });
  const routes = data?.routes || [];
  const { deleteRoute, isLoading: deleting } =
    useEticketingDeleteRouteMutation();

  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [routeToEdit, setRouteToEdit] = useState(null);

  const itemsPerPage = 10;

  const filteredRoutes = routes.filter((route) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      route.name?.toLowerCase().includes(lowerSearch) ||
      route.id?.toString().includes(lowerSearch)
    );
  });

  const totalPages = Math.ceil(filteredRoutes.length / itemsPerPage);
  const paginatedRoutes = filteredRoutes.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handleDelete = (route) => {
    setRouteToDelete(route);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!routeToDelete) return;

    try {
      await deleteRoute(routeToDelete.id);
      setSuccessMsg("Route deleted successfully.");
      refetch();
      setShowDeleteModal(false);
      setRouteToDelete(null);
    } catch (err) {
      setErrorMsg(err?.message || "Failed to delete route.");
      setShowDeleteModal(false);
      setRouteToDelete(null);
    } finally {
      setTimeout(() => {
        setErrorMsg(null);
        setSuccessMsg(null);
      }, 3000);
    }
  };

  const handleEdit = (route) => {
    setRouteToEdit(route);
    setShowForm(true);
  };

  const handleAddRoute = () => {
    setRouteToEdit(null);
    setShowForm(true);
  };

  const handleFormSuccess = (message) => {
    setSuccessMsg(message);
    setShowForm(false);
    setRouteToEdit(null);
    refetch();
    setTimeout(() => {
      setSuccessMsg(null);
    }, 3000);
  };

  const handleFormError = (message) => {
    setErrorMsg(message);
    setTimeout(() => {
      setErrorMsg(null);
    }, 3000);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setRouteToEdit(null);
  };

  const handleViewPrices = (routeId) => {
    setSelectedRouteId(selectedRouteId === routeId ? null : routeId);
  };

  return (
    <div className="col-12">
      {successMsg && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccessMsg(null)}
        >
          {emojis.success} {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert variant="danger" dismissible onClose={() => setErrorMsg(null)}>
          {emojis.warning} {errorMsg}
        </Alert>
      )}

      {!selectedRouteId && !showForm && (
        <div className="card">
          {/* Header */}
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title m-0">Routes & Pricing</h4>
            <div className="d-flex align-items-center gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search by route name or ID"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
              <Can I="create" a="Route">
                <button
                  className="btn btn-secondary"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={handleAddRoute}
                >
                  Add Route
                </button>
              </Can>
            </div>
          </div>

          {/* Table */}
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center p-4">
                <Spinner animation="border" role="status" />
              </div>
            ) : error ? (
              <Alert variant="danger">
                {emojis.warning} Failed to load routes.
              </Alert>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-striped align-middle">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Route Name</th>
                        <th style={{ maxWidth: "200px" }}>Description</th>
                        <th>Image</th>
                        <th>City</th>
                        {/* <th>Service ID</th> */}
                        <th>Route Prices</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRoutes.length > 0 ? (
                        paginatedRoutes.map((route, i) => (
                          <tr key={route.id}>
                            <td>{currentPage * itemsPerPage + i + 1}</td>
                            <td>{route.name || "--"}</td>
                            <td
                              style={{
                                maxWidth: "200px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {route.description ? (
                                <span title={route.description}>
                                  {route.description.length > 50
                                    ? route.description.substring(0, 50) + "..."
                                    : route.description}
                                </span>
                              ) : (
                                "--"
                              )}
                            </td>
                            <td>
                              {route.image ? (
                                <img
                                  src={route.image}
                                  alt={route.name}
                                  style={{
                                    width: "50px",
                                    height: "50px",
                                    objectFit: "cover",
                                    borderRadius: "4px",
                                  }}
                                />
                              ) : (
                                <span className="text-muted">No image</span>
                              )}
                            </td>
                            <td>{route.cityName || "--"}</td>
                            {/* <td>{route.service_id || "--"}</td> */}
                            <td>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleViewPrices(route.id)}
                                title="View Prices"
                              >
                                <i className="fa fa-eye"></i>
                              </button>
                            </td>
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle variant="link">
                                  &#x2022;&#x2022;&#x2022;
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                  <Dropdown.Item
                                    onClick={() => handleEdit(route)}
                                  >
                                    Edit
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleDelete(route)}
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center">
                            No routes found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {filteredRoutes.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    itemsPerPage={itemsPerPage}
                    totalItems={filteredRoutes.length}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <RouteForm
          route={routeToEdit}
          onSuccess={handleFormSuccess}
          onError={handleFormError}
          onCancel={handleCancelForm}
          serviceId={serviceId}
          service={service}
        />
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setRouteToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Route"
        message={`Are you sure you want to delete the route "${routeToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleting}
        danger={true}
      />

      {selectedRouteId && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title m-0">
              Route Prices - Route ID: {selectedRouteId}
            </h5>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setSelectedRouteId(null)}
            >
              <i className="fa fa-times me-1"></i> Close
            </button>
          </div>
          <div className="card-body">
            <RoutePrices routeId={selectedRouteId} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Routes;
